import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import styles from './Dashboard.module.css';
import CorrectionMode from '../CorrectionMode/CorrectionMode';
import ConstructionMode from '../ConstructionMode/ConstructionMode';
import StoryMode from '../StoryMode/StoryMode';
import AdaptiveMode from '../AdaptiveMode/AdaptiveMode';
import AITutor from '../AITutor/AITutor';
import StatsView from '../StatsView/StatsView';
import AppSelector from '../AppSelector/AppSelector';
import WritingDashboard from '../../pages/WritingDashboard';
import SpellingProgress from '../SpellingProgress/SpellingProgress';
import VocabModes from '../VocabModes';

const Dashboard = () => {
  const { userProgress, loading, error, selectedApp, selectApp } = useUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('modes');
  const [selectedMode, setSelectedMode] = useState(null);
  const [showTutor, setShowTutor] = useState(true);
  const [userLevel, setUserLevel] = useState(1);

  useEffect(() => {
    if (selectedApp === 'grammar') {
      setActiveTab('modes');
    }
  }, [selectApp]);

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  // If no app is selected, show the app selector
  if (!selectedApp) {
    return <AppSelector onSelect={selectApp} recommendedPaths={userProgress?.recommendedLearningPaths} />;
  }

  // If writing app is selected, show the writing dashboard
  if (selectedApp === 'writing') {
    return <WritingDashboard />;
  }

  const stats = [
    {
      label: 'Current Level',
      value: selectedApp === 'grammar' 
        ? userProgress?.grammarStats?.currentLevel || 1 
        : selectedApp === 'spelling' 
          ? userProgress?.spellingStats?.currentLevel || 1
          : userProgress?.vocabularyStats?.currentLevel || 1,
      icon: '🎯',
      iconClass: styles.iconBlue
    },
    {
      label: 'Practice Time',
      value: `${selectedApp === 'grammar' 
        ? userProgress?.grammarStats?.totalPracticeHours || 0 
        : selectedApp === 'spelling' 
          ? userProgress?.spellingStats?.totalPracticeHours || 0
          : userProgress?.vocabularyStats?.totalPracticeHours || 0}h`,
      icon: '⏱️',
      iconClass: styles.iconGreen
    },
    {
      label: 'Lessons Completed',
      value: selectedApp === 'grammar' 
        ? userProgress?.grammarStats?.totalSessions || 0 
        : selectedApp === 'spelling' 
          ? userProgress?.spellingStats?.totalSessions || 0
          : userProgress?.vocabularyStats?.totalSessions || 0,
      icon: '📚',
      iconClass: styles.iconPurple
    },
    // Only show areas to improve for grammar and vocabulary modes, not for spelling
    ...(selectedApp !== 'spelling' ? [{
      label: 'Areas to Improve',
      value: selectedApp === 'grammar' 
        ? userProgress?.areasToImprove?.length || 0 
        : selectedApp === 'vocabulary'
          ? userProgress?.vocabularyAreasToImprove?.length || 0
          : 0,
      icon: '⚠️',
      iconClass: styles.iconYellow,
      isFlippable: true,
      flipContent: selectedApp === 'grammar' && userProgress?.areasToImprove?.length > 0 
        ? userProgress.areasToImprove 
        : selectedApp === 'vocabulary' && userProgress?.vocabularyAreasToImprove?.length > 0
          ? userProgress.vocabularyAreasToImprove
          : 'No areas to improve yet'
    }] : [])
  ];

  // Define modes based on selected app
  const modes = selectedApp === 'grammar' ? [
    {
      title: 'Correction Mode',
      description: 'Practice identifying and fixing grammar mistakes',
      icon: '🎯',
      level: userProgress?.modeLevels?.correction || 1,
      mode: 'correction'
    },
    {
      title: 'Construction Mode',
      description: 'Build grammatically correct sentences',
      icon: '🏗️',
      level: userProgress?.modeLevels?.construction || 1,
      mode: 'construction'
    },
    {
      title: 'Story Mode',
      description: 'Learn grammar through interactive stories',
      icon: '📚',
      level: userProgress?.modeLevels?.story || 1,
      mode: 'story'
    },
    {
      title: 'Adaptive Learning',
      description: 'Personalized practice based on your performance',
      icon: '🎓',
      level: userProgress?.modeLevels?.adaptive || 1,
      mode: 'adaptive'
    },
    {
      title: 'Exam Mode',
      description: 'Test your grammar knowledge',
      icon: '📝',
      level: userProgress?.modeLevels?.exam || 1,
      mode: 'exam'
    },
    // {
    //   title: 'Timed Challenge',
    //   description: 'Race against time to solve grammar questions',
    //   icon: '⏱️',
    //   level: userProgress?.modeLevels?.timed || 1,
    //   mode: 'timed'
    // }
  ] : selectedApp === 'spelling' ? [
    {
      title: 'Word Completion',
      description: 'Fill in missing letters to complete words',
      icon: '📝',
      level: userProgress?.modeLevels?.word_completion || 1,
      mode: 'word_completion'
    },
    {
      title: 'Audio Spelling',
      description: 'Listen and spell words correctly',
      icon: '🔊',
      level: userProgress?.modeLevels?.audio_spelling || 1,
      mode: 'audio_spelling'
    },
    {
      title: 'Anagram Challenge',
      description: 'Unscramble letters to find the correct word',
      icon: '🔄',
      level: userProgress?.modeLevels?.anagram_challenge || 1,
      mode: 'anagram_challenge'
    },
    {
      title: 'Definition Spelling',
      description: 'Spell words based on their definitions',
      icon: '📚',
      level: userProgress?.modeLevels?.definition_spelling || 1,
      mode: 'definition_spelling'
    },
    {
      title: 'Spelling Bee',
      description: 'Test your spelling skills under time pressure',
      icon: '⏱️',
      level: userProgress?.modeLevels?.spelling_bee || 1,
      mode: 'spelling_bee'
    },
    {
      title: 'Story Spelling',
      description: 'Practice spelling words in engaging stories',
      icon: '📖',
      level: userProgress?.modeLevels?.story_spelling || 1,
      mode: 'story_spelling'
    }
  ] : [
    // Vocabulary app modes
    {
      title: 'Learn & Practice',
      description: 'Learn new words through flashcards, sentence usage, and word association',
      icon: '📖',
      level: userProgress?.modeLevels?.learn_practice || 1,
      mode: 'learn_practice'
    },
    {
      title: 'Memory Techniques',
      description: 'Use memory palace, sound mnemonics, and chunking to remember words',
      icon: '🧠',
      level: userProgress?.modeLevels?.memory_techniques || 1,
      mode: 'memory_techniques'
    },
    {
      title: 'Speaking & Listening',
      description: 'Improve pronunciation and comprehension through active usage',
      icon: '🎤',
      level: userProgress?.modeLevels?.speaking_listening || 1,
      mode: 'speaking_listening'
    },
    {
      title: 'Writing Prompts',
      description: 'Use new vocabulary in creative writing exercises',
      icon: '✏️',
      level: userProgress?.modeLevels?.writing_prompts || 1,
      mode: 'writing_prompts'
    }
  ];

  const handleModeComplete = (results) => {
    // Handle completion of mode (you can add stats update logic here)
    setSelectedMode(null); // Return to dashboard
  };

  const handleModeSelect = (mode, difficulty) => {
    const modeLevel = userProgress?.modeLevels?.[mode] || 1;
    let selectedLevel;

    switch (difficulty) {
      case 'easy':
        selectedLevel = Math.max(modeLevel - 1, 1);
        break;
      case 'hard':
        selectedLevel = Math.min(modeLevel + 1, 7);
        break;
      default:
        selectedLevel = modeLevel;
    }

    navigate(`/mode/${mode}`, { 
      state: { 
        difficulty: selectedLevel,
        mode 
      } 
    });
  };

  const renderModes = () => {
    if (selectedApp === 'vocabulary'){
      return <VocabModes />
    }
    else{
      return (
        <div className={styles.modesGrid}>
          {modes.map((mode, index) => (
        <div key={index} className={styles.modeCard}>
          <div className={styles.modeHeader}>
            <span className={styles.modeIcon}>{mode.icon}</span>
            <div className={styles.modeTitleContainer}>
              <h3 className={styles.modeTitle}>{mode.title}</h3>
              <div className={styles.levelBadge}>Level {mode.level}</div>
            </div>
          </div>
          <p className={styles.modeDescription}>{mode.description}</p>
          <div className={styles.difficultyOptions}>
            <button 
              className={`${styles.difficultyButton} ${styles.easy}`}
              onClick={() => handleModeSelect(mode.mode, 'easy')}
            >
              Easy (Level {Math.max(mode.level - 1, 1)})
            </button>
            <button 
              className={`${styles.difficultyButton} ${styles.medium}`}
              onClick={() => handleModeSelect(mode.mode, 'medium')}
            >
              Medium (Level {mode.level})
            </button>
            <button 
              className={`${styles.difficultyButton} ${styles.hard}`}
              onClick={() => handleModeSelect(mode.mode, 'hard')}
            >
              Hard (Level {Math.min(mode.level + 1, 7)})
            </button>
          </div>
        </div>
      ))}
        </div>
      )
    }
  };

  const renderProgress = () => {
    if (selectedApp === 'grammar') {
      // return (
      //   <div className={styles.progressSection}>
      //     <h3 className={styles.title}>Performance by Category</h3>
      //     {Object.entries(userProgress?.categoryScores || {}).map(([category, score]) => (
      //       <div key={category} className={styles.categoryProgress}>
      //         <div className={styles.categoryHeader}>
      //           <span className={styles.categoryLabel}>
      //             {category.charAt(0).toUpperCase() + category.slice(1)}
      //           </span>
      //           <span className={styles.categoryScore}>{score}%</span>
      //         </div>
      //         <div className={styles.progressBar}>
      //           <div 
      //             className={styles.progressFill} 
      //             style={{ width: `${score}%` }}
      //           />
      //         </div>
      //       </div>
      //     ))}
      //   </div>
      // );
    } else if (selectedApp === 'spelling') {
      return <SpellingProgress />;
    } 
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          {selectedApp === 'grammar' ? 'Grammarly' : 
           selectedApp === 'spelling' ? 'Spelling Pro' : 'Vocabulary Builder'}
        </h1>
      </div>

      <div className={styles.statsGrid}>
        {stats.map((stat, index) => (
          <div key={index} className={styles.statCard}>
            {stat.isFlippable ? (
              <div className={styles.flipCard}>
                <div className={styles.flipCardInner}>
                  <div className={styles.flipCardFront}>
                    <div className={styles.statContent}>
                      <div className={`${styles.statIcon} ${stat.iconClass}`}>
                        {stat.icon}
                      </div>
                      <div>
                        <p className={styles.statLabel}>{stat.label}</p>
                        <p className={styles.statValue}>{stat.value}</p>
                      </div>
                    </div>
                  </div>
                  <div className={styles.flipCardBack}>
                    {Array.isArray(stat.flipContent) ? (
                      <ul>
                        {stat.flipContent.map(i => i.area).map(item => (
                          <li key={item} className={styles.areaItem}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className={styles.noAreasMessage}>{stat.flipContent}</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.statContent}>
                <div className={`${styles.statIcon} ${stat.iconClass}`}>
                  {stat.icon}
                </div>
                <div>
                  <p className={styles.statLabel}>{stat.label}</p>
                  <p className={styles.statValue}>{stat.value}</p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedApp !== 'vocabulary' && (
      <div className={styles.tabs}>
        <div className={styles.tabList}>
          <button
            className={`${styles.tabButton} ${activeTab === 'modes' ? styles.active : ''}`}
            onClick={() => setActiveTab('modes')}
          >
            Learning Modes
          </button>
          {
            selectedApp === 'spelling' && (  
              <button
                className={`${styles.tabButton} ${activeTab === 'progress' ? styles.active : ''}`}
                onClick={() => setActiveTab('progress')}
              >
                Progress
              </button>
            )
          }
          <button
            className={`${styles.tabButton} ${activeTab === 'stats' ? styles.active : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            Statistics
          </button>
        </div>
        {activeTab === 'progress' && renderProgress()}
        {activeTab === 'stats' && <StatsView />}
      </div>
      )}

        {activeTab === 'modes' && renderModes()}

        <AITutor 
          currentQuestion={{
            content: {
              text: "Welcome to the Grammar Learning App! How can I help you today?",
              correctAnswer: null
            }
          }}
          userAnswer={null}
          isCorrect={null}
          isDashboard={true}
        />
    </div>
  );
};

export default Dashboard;